import { Brain, Music, Check } from "lucide-react";
import { TESTIDS, SCHEDULE_FORM_URL } from "@/lib/site";
import { trackWhatsAppClick } from "@/lib/analytics";
import { useReveal } from "@/lib/useReveal";

const PSYCH_TOPICS = [
  "Ansiedad",
  "Depresión",
  "Autoestima",
  "Relaciones",
  "Crisis personales",
  "Agotamiento emocional",
  "Transiciones de vida",
  "Neurodivergencia",
  "Acompañamiento emocional",
];

const MT_TOOLS = [
  "Escucha musical",
  "Voz",
  "Improvisación",
  "Expresión emocional",
  "Instrumentos",
  "Creatividad",
  "Exploración simbólica",
];

export default function Services() {
  const r = useReveal();
  return (
    <section
      id="servicios"
      data-testid={TESTIDS.services.section}
      className="relative py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className={`${r.className} max-w-2xl mb-14`}>
          <p className="text-[0.78rem] tracking-[0.22em] uppercase text-accent-magenta mb-4">
            Servicios
          </p>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-[3.4rem] text-editorial-dark leading-[1.05]">
            Dos caminos para
            <span className="italic text-terracotta"> escucharte</span>.
          </h2>
          <p className="mt-5 text-editorial-gray text-[1.02rem] leading-relaxed">
            Espacios terapéuticos diseñados para sostener tu proceso con tiempo, respeto y
            mirada profesional.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-7">
          {/* Psychotherapy */}
          <article
            data-testid={TESTIDS.services.cardPsychotherapy}
            className="group relative overflow-hidden rounded-[2rem] bg-dusty-rose/25 border border-dusty-rose/40 p-9 md:p-12 hover:-translate-y-1 transition-all duration-500"
          >
            <div className="flex items-center gap-3 text-accent-magenta">
              <span className="inline-flex w-12 h-12 rounded-full bg-warm-white items-center justify-center shadow-sm">
                <Brain size={22} />
              </span>
              <span className="text-[0.78rem] tracking-[0.22em] uppercase">Servicio 01</span>
            </div>
            <h3 className="font-serif text-3xl md:text-[2.4rem] text-editorial-dark mt-6 leading-tight">
              Psicoterapia individual
            </h3>
            <p className="mt-3 text-editorial-gray leading-relaxed">
              Un proceso uno a uno, en un marco confidencial, para mirar lo que duele y abrir
              nuevas formas de habitarte.
            </p>
            <div className="mt-7 grid grid-cols-2 gap-x-4 gap-y-2.5">
              {PSYCH_TOPICS.map((t) => (
                <p key={t} className="flex items-center gap-2 text-[0.93rem] text-editorial-dark">
                  <Check size={14} className="text-accent-magenta shrink-0" />
                  {t}
                </p>
              ))}
            </div>
            <a
              href={SCHEDULE_FORM_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackWhatsAppClick("services_psychotherapy")}
              className="mt-9 inline-flex items-center gap-2 rounded-full bg-accent-magenta text-warm-white px-6 py-3 text-sm hover:-translate-y-0.5 transition-all shadow-sm shadow-accent-magenta/20"
            >
              Agendar consulta por WhatsApp
              <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14M13 5l7 7-7 7" />
              </svg>
            </a>
            {/* Decorative line */}
            <svg className="absolute -bottom-1 -right-1 w-56 opacity-40" viewBox="0 0 200 40" fill="none" aria-hidden>
              <path d="M0 30 C 40 5, 80 35, 120 15 S 200 25, 200 25" stroke="#6A004D" strokeWidth="1" />
            </svg>
          </article>

          {/* Music Therapy */}
          <article
            data-testid={TESTIDS.services.cardMusicTherapy}
            className="group relative overflow-hidden rounded-[2rem] bg-sage/25 border border-sage/40 p-9 md:p-12 hover:-translate-y-1 transition-all duration-500"
          >
            <div className="flex items-center gap-3 text-[#3F5340]">
              <span className="inline-flex w-12 h-12 rounded-full bg-warm-white items-center justify-center shadow-sm">
                <Music size={22} />
              </span>
              <span className="text-[0.78rem] tracking-[0.22em] uppercase">Servicio 02</span>
            </div>
            <h3 className="font-serif text-3xl md:text-[2.4rem] text-editorial-dark mt-6 leading-tight">
              Musicoterapia humanista
            </h3>
            <p className="mt-3 text-editorial-gray leading-relaxed">
              Un modelo 100% mexicano que ha ayudado a miles de personas a comunicar
              emociones y pensamientos por vías no verbales, resignificar experiencias y
              construir una vida más auténtica.
            </p>

            <div className="mt-6 rounded-2xl bg-warm-white/60 border border-sage/30 p-5">
              <p className="text-[0.78rem] tracking-[0.18em] uppercase text-[#3F5340] mb-3">
                Importante
              </p>
              <ul className="space-y-2 text-[0.95rem] text-editorial-dark">
                <li className="flex gap-2"><Check size={14} className="text-accent-magenta shrink-0 mt-1" /> No se requieren conocimientos musicales.</li>
                <li className="flex gap-2"><Check size={14} className="text-accent-magenta shrink-0 mt-1" /> No es una clase de música.</li>
                <li className="flex gap-2"><Check size={14} className="text-accent-magenta shrink-0 mt-1" /> Es un espacio terapéutico.</li>
              </ul>
            </div>

            <div className="mt-6">
              <p className="text-sm tracking-widest uppercase text-editorial-gray mb-3">
                Recursos terapéuticos
              </p>
              <div className="flex flex-wrap gap-2">
                {MT_TOOLS.map((t) => (
                  <span
                    key={t}
                    className="rounded-full bg-warm-white border border-sage/40 px-3.5 py-1.5 text-sm text-editorial-dark"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>

            <a
              href={SCHEDULE_FORM_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackWhatsAppClick("services_music_therapy")}
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-editorial-dark text-warm-white px-6 py-3 text-sm hover:-translate-y-0.5 transition-all"
            >
              Explorar este espacio por WhatsApp
              <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14M13 5l7 7-7 7" />
              </svg>
            </a>
          </article>
        </div>
      </div>
    </section>
  );
}
